import { useMemo } from 'react';
import { Location } from './types';
import { LocationNode } from './types';

export const useLocationTree = (locations: Location[]): LocationNode[] => {
  return useMemo(() => {
    const countriesMap = new Map<string, LocationNode>();
    const statesMap = new Map<string, LocationNode>();

    locations.forEach((loc) => {
      const countryKey = loc.countryCode || loc.country || 'UNKNOWN';
      const stateKey = loc.stateCode || loc.state || '';

      // --- Country node ---
      if (!countriesMap.has(countryKey)) {
        countriesMap.set(countryKey, {
          id: `country-${countryKey}`,
          label: loc.country || countryKey,
          level: 'country',
          children: [],
        });
      }
      const countryNode = countriesMap.get(countryKey)!;

      // --- State node (if exists) ---
      if (stateKey) {
        const stateId = `state-${countryKey}-${stateKey}`;
        if (!statesMap.has(stateId)) {
          const stateNode: LocationNode = {
            id: stateId,
            label: loc.state || stateKey,
            level: 'state',
            parentId: countryNode.id,
            children: [],
          };
          statesMap.set(stateId, stateNode);
          countryNode.children!.push(stateNode);
        }
        const stateNode = statesMap.get(`state-${countryKey}-${stateKey}`)!;

        // --- City node ---
        stateNode.children!.push({
          id: loc.id,
          label: loc.name,
          level: 'city',
          parentId: stateNode.id,
          locationId: loc.id,
        });
      } else {
        // City directly under country
        countryNode.children!.push({
          id: loc.id,
          label: loc.name,
          level: 'city',
          parentId: countryNode.id,
          locationId: loc.id,
        });
      }
    });

    // Sort states by state code and cities by name
    countriesMap.forEach((countryNode) => {
      if (countryNode.children) {
        // Sort states by state code (extract from id: "state-{country}-{stateCode}")
        countryNode.children.sort((a, b) => {
          if (a.level === 'state' && b.level === 'state') {
            const stateCodeA = a.id.split('-').pop() || '';
            const stateCodeB = b.id.split('-').pop() || '';
            return stateCodeA.localeCompare(stateCodeB);
          }
          // Cities by name
          return a.label.localeCompare(b.label);
        });

        // Sort cities within each state by name
        countryNode.children.forEach((stateNode) => {
          if (stateNode.level === 'state' && stateNode.children) {
            stateNode.children.sort((a, b) => a.label.localeCompare(b.label));
          }
        });
      }
    });

    return Array.from(countriesMap.entries())
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([, node]) => node);
  }, [locations]);
};
